import { Landmark, TrendingUp, TrendingDown, Building2 } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { formatCurrency } from "@/utils/format";
import { BankAccount }    from "@/hooks/useBankBalance";
import { cn } from "@/utils/cn";

interface Props {
  open:        boolean;
  onClose:     () => void;
  total:       number;
  balanceDate: string;
  accounts:    BankAccount[];
}

function formatDateBR(iso: string) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function BankBalanceModal({ open, onClose, total, balanceDate, accounts }: Props) {
  /* Separa contas ativas das zeradas/desativadas e ordena por valor decrescente */
  const active   = accounts.filter((a) => a.accountStatus === "ENABLED" && a.amount !== 0)
                            .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  const inactive = accounts.filter((a) => a.accountStatus !== "ENABLED" || a.amount === 0);

  const totalPositive = active.filter((a) => a.amount > 0).reduce((s, a) => s + a.amount, 0);
  const totalNegative = active.filter((a) => a.amount < 0).reduce((s, a) => s + a.amount, 0);
  const maxAbs        = active.reduce((m, a) => Math.max(m, Math.abs(a.amount)), 1);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
            <Landmark className="w-4 h-4 text-violet-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800 leading-tight">Saldo Bancário</p>
            <p className="text-xs text-slate-400">Posição em {formatDateBR(balanceDate)}</p>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5">

        {/* ── Resumo ── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1 flex flex-col gap-1 bg-violet-50 rounded-xl px-4 py-3 border border-violet-100">
            <p className="text-[11px] text-violet-600 font-semibold uppercase tracking-wide">Saldo Total</p>
            <p className={cn(
              "text-xl font-bold leading-tight",
              total < 0 ? "text-red-600" : "text-violet-800"
            )}>
              {formatCurrency(total)}
            </p>
          </div>
          <div className="flex flex-col gap-1 bg-emerald-50 rounded-xl px-4 py-3 border border-emerald-100">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              <p className="text-[11px] text-emerald-600 font-semibold uppercase tracking-wide">Positivo</p>
            </div>
            <p className="text-base font-bold text-emerald-700 leading-tight">{formatCurrency(totalPositive)}</p>
          </div>
          <div className="flex flex-col gap-1 bg-red-50 rounded-xl px-4 py-3 border border-red-100">
            <div className="flex items-center gap-1.5">
              <TrendingDown className="w-3.5 h-3.5 text-red-500" />
              <p className="text-[11px] text-red-500 font-semibold uppercase tracking-wide">Negativo</p>
            </div>
            <p className="text-base font-bold text-red-600 leading-tight">{formatCurrency(totalNegative)}</p>
          </div>
        </div>

        {/* ── Lista de contas ativas ── */}
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1 mb-1">
            Contas com Saldo ({active.length})
          </p>

          {active.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-6">Nenhuma conta com saldo encontrada.</p>
          )}

          {active.map((acc, idx) => {
            const pct     = maxAbs > 0 ? (Math.abs(acc.amount) / maxAbs) * 100 : 0;
            const isNeg   = acc.amount < 0;
            return (
              <div
                key={`${acc.accountNumber}-${acc.companyId}-${idx}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
              >
                {/* Ícone + conta */}
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                  isNeg ? "bg-red-50" : "bg-violet-50"
                )}>
                  <Building2 className={cn("w-4 h-4", isNeg ? "text-red-500" : "text-violet-500")} />
                </div>

                {/* Nome + barra */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate">
                        {acc.displayName || acc.accountNumber}
                      </p>
                      {acc.displayName && (
                        <p className="text-[10px] text-slate-400 truncate font-mono">{acc.accountNumber}</p>
                      )}
                    </div>
                    {acc.companyId && (
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">Emp. {acc.companyId}</span>
                    )}
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={cn("h-full rounded-full transition-all", isNeg ? "bg-red-400" : "bg-violet-400")}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                {/* Valor */}
                <span className={cn(
                  "text-sm font-bold tabular-nums whitespace-nowrap w-32 text-right",
                  isNeg ? "text-red-600" : "text-slate-800"
                )}>
                  {formatCurrency(acc.amount)}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Contas zeradas/inativas (colapsado) ── */}
        {inactive.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600 transition-colors select-none list-none flex items-center gap-1.5 px-1">
              <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
              {inactive.length} conta{inactive.length !== 1 ? "s" : ""} zerada{inactive.length !== 1 ? "s" : ""} ou inativa{inactive.length !== 1 ? "s" : ""}
            </summary>
            <div className="mt-2 flex flex-col gap-0.5 pl-4">
              {inactive.map((acc, idx) => (
                <div key={`${acc.accountNumber}-${idx}`} className="flex items-center justify-between px-3 py-1.5 rounded-lg text-slate-400">
                  <span className="text-xs">{acc.displayName || acc.accountNumber}</span>
                  <span className="text-xs tabular-nums">{formatCurrency(acc.amount)}</span>
                </div>
              ))}
            </div>
          </details>
        )}

      </div>
    </Modal>
  );
}
